import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Tooltip, Typography, useTheme } from '@mui/material'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { useGetProjectForMap } from 'src/api/project'
import { useApplyOlexMatch, usePreviewOlexMatch } from 'src/api/projectVersionPositions'
import { ReactComponent as OlexFileIcon } from 'src/assets/svg/OlextFileIcon.svg'
import { CancelButton } from 'src/components/CancelButton'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { CommonFileUploader } from 'src/components/common/input/CommonFileUploader'
import { AppForm } from 'src/components/FormCore'
import { MainButton } from 'src/components/MainButton'
import { DialogSpinner } from 'src/components/Spinner'
import { IMooringType } from 'src/generated-types'
import { IOlexMatchPreview, IOlexMatchStatus } from 'src/generated-types/olex-match'
import { useZodForm } from 'src/hooks/zod-form'
import { MUIThemeColors } from 'src/mui-theme'
import { MapComponent } from 'src/screens/localities/components/map/MapComponent'
import { getThemeColor } from 'src/utils'
import {
  DEFAULT_OLEX_CHOICES,
  OLEX_ACCEPTED_TYPES,
  OlexMapHighlight as Highlight,
  OlexMatchChoices,
  hasOlexChanges,
  isOlexNameUsable,
  olexFormData,
  olexMatchStatusKey,
  withManualMove,
} from './olexMatch.action'
import { PanelCard } from './ExpandGrid.style'
import OlexManualMoves from './OlexManualMoves'
import OlexMapHighlight from './OlexMapHighlight'
import OlexMatchSummary from './OlexMatchSummary'
import OlexNameChoice from './OlexNameChoice'
import { refreshProjectData } from './refreshProjectData'
import {
  ActionRow,
  AsideActions,
  AsideHead,
  AsideScroll,
  LegendItem,
  ModeAside,
  ModeCanvas,
  ModeColumn,
  ModeLegend,
  ModeTag,
} from './RevisionModeLayout'
import { RevisionConfigure } from './useRevisionConfigure'
import { useProjectsT } from './useProjectsT'

interface OlexUploadModeProps {
  revision: RevisionConfigure
  // The revision toolbar, which stays above the preview.
  toolbar: React.ReactNode
  onClose: () => void
}

// Uploads a re-surveyed Olex file into the draft, in place of the configure drawing. Positions found at their place keep
// their name and setup; new ones get the next free names, and positions missing from the file are removed.
const OlexUploadMode: React.FC<OlexUploadModeProps> = ({ revision, toolbar, onClose }) => {
  const { project, olexRequest, consumeOlexRequest, onOlexApplied } = revision
  const t = useProjectsT()
  const theme = useTheme()
  const [file, setFile] = useState<File | undefined>()
  const [preview, setPreview] = useState<IOlexMatchPreview | undefined>()
  const [pickerKey, setPickerKey] = useState(0)
  const [choices, setChoices] = useState<OlexMatchChoices>(DEFAULT_OLEX_CHOICES)
  const [highlight, setHighlight] = useState<Highlight>()
  const form = useZodForm(z.object({ fileIds: z.any() }), {})
  const ids = [project.customerId, project.localityId, project.id] as const
  const { mutateAsync: previewMatch, isLoading: isPreviewing } = usePreviewOlexMatch(...ids)
  const { mutateAsync: applyMatch, isLoading: isApplying } = useApplyOlexMatch(...ids)
  const { data: current } = useGetProjectForMap(...ids)

  // A file the project setup already matched opens straight on its preview.
  useEffect(() => {
    if (!olexRequest) return
    setFile(olexRequest.file)
    setPreview(olexRequest.preview)
    setChoices(DEFAULT_OLEX_CHOICES)
    consumeOlexRequest()
  }, [olexRequest, consumeOlexRequest])

  const ready = preview?.status === IOlexMatchStatus.Ready
  const statusKey = preview ? olexMatchStatusKey(preview.status) : undefined
  // The draft as it is now stays visible, faded, under what the upload makes of it.
  const mapPositions = useMemo(
    () =>
      ready && preview
        ? [...(current?.positions ?? []).map(p => ({ ...p, isTransparent: true })), ...preview.positions]
        : [],
    [ready, preview, current],
  )

  const runPreview = async (picked: File, next: OlexMatchChoices) => {
    setChoices(next)
    try {
      const { data } = await previewMatch(olexFormData(picked, next))
      setPreview(data as unknown as IOlexMatchPreview)
      return true
    } catch {
      toast.error(t('revision-olex-failed'))
      return false
    }
  }

  const choose = async (picked: File) => {
    setFile(picked)
    setPreview(undefined)
    if (!(await runPreview(picked, DEFAULT_OLEX_CHOICES))) setPickerKey(key => key + 1)
  }

  const changeChoices = (patch: Partial<OlexMatchChoices>) => file && runPreview(file, { ...choices, ...patch })

  const reset = () => {
    setFile(undefined)
    setPreview(undefined)
    setChoices(DEFAULT_OLEX_CHOICES)
    setHighlight(undefined)
    setPickerKey(key => key + 1)
  }

  const handleApply = async () => {
    if (!file || !preview) return
    try {
      await applyMatch(olexFormData(file, choices))
      toast.success(t('revision-olex-done'))
      refreshProjectData(project.id)
      onOlexApplied?.(preview)
      onClose()
    } catch {
      toast.error(t('revision-olex-failed'))
    }
  }

  const removed = preview?.groups.reduce((sum, g) => sum + g.removed.length, 0) ?? 0
  const addedCount = preview?.groups.reduce((sum, g) => sum + g.added.length, 0) ?? 0
  const renamed = preview?.useOlexNames ? preview.names.filter(n => isOlexNameUsable(n.issue)).length : 0

  return (
    <>
      <ModeColumn>
        {toolbar}
        {isPreviewing && <DialogSpinner />}
        <ModeCanvas>
          {ready && preview ? (
            <>
              <ModeTag>{t('revision-olex-map-legend')}</ModeTag>
              <Box position="absolute" top={0} right={0} bottom={0} left={0}>
                <MapComponent
                  key="olex-revision-map"
                  positions={mapPositions}
                  mooringType={project.type as IMooringType}
                  layerControl={false}
                  zoomControl={true}
                  attributionControl={false}
                  scrollWheelZoom={true}
                >
                  <OlexMapHighlight
                    current={current?.positions ?? []}
                    preview={preview.positions}
                    highlight={highlight}
                  />
                </MapComponent>
              </Box>
            </>
          ) : (
            <Box display="flex" height="100%" alignItems="center" justifyContent="center" padding={3}>
              <Typography color="text.secondary" textAlign="center" maxWidth={420}>
                {t('revision-olex-empty-canvas')}
              </Typography>
            </Box>
          )}
        </ModeCanvas>
        <ModeLegend>
          <span>{t('revision-olex-hint-scroll')}</span>
          <span>{t('revision-olex-hint-drag')}</span>
          <LegendItem color={getThemeColor(theme, MUIThemeColors.successMain)}>
            {t('revision-olex-legend-added')}
          </LegendItem>
          <LegendItem color={getThemeColor(theme, MUIThemeColors.errorMain)}>
            {t('revision-olex-legend-removed')}
          </LegendItem>
        </ModeLegend>
      </ModeColumn>
      <ModeAside>
        <AsideHead>
          <GridActionButton
            border={0}
            shadowWeight={null}
            buttonText={`← ${t('revision-mode-back')}`}
            buttonTextProps={{ fontSize: 12 }}
            onClick={onClose}
            sx={{ alignSelf: 'flex-start', padding: 0, minWidth: 0 }}
          />
          <Typography variant="h6" fontWeight={600}>
            {t('revision-olex-title')}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {t('revision-olex-description')}
          </Typography>
        </AsideHead>
        <AsideScroll>
          {!file && (
            <AppForm form={form}>
              <CommonFileUploader
                key={pickerKey}
                name="fileIds"
                acceptedFileTypes={OLEX_ACCEPTED_TYPES}
                onChange={() => {}}
                FileIcon={() => <OlexFileIcon width="50px" height="50px" />}
                onUploadFile={choose}
                isFileServerUpload={false}
                height="150px"
                isDeletable={false}
              />
            </AppForm>
          )}
          {file && (
            <PanelCard>
              <Box display="flex" alignItems="center" gap={1.25}>
                <OlexFileIcon width="28px" height="28px" style={{ flex: 'none' }} />
                <Tooltip title={file.name}>
                  <Typography variant="body2" fontWeight={600} noWrap flex={1} minWidth={0}>
                    {file.name}
                  </Typography>
                </Tooltip>
                <GridActionButton
                  buttonText={t('revision-olex-other-file')}
                  buttonTextProps={{ fontSize: 12 }}
                  height="30px"
                  border={1}
                  onClick={reset}
                  sx={{ flex: 'none' }}
                />
              </Box>
            </PanelCard>
          )}
          {statusKey && (
            <Alert severity={preview?.status === IOlexMatchStatus.NoOverlap ? 'info' : 'warning'}>
              {t(statusKey, { names: preview?.offGrid.join(', ') })}
            </Alert>
          )}
          {ready && preview && (
            <>
              {!hasOlexChanges(preview) && <Alert severity="success">{t('revision-olex-no-changes')}</Alert>}
              <OlexMatchSummary preview={preview} t={t} />
              {preview.movableLines.length > 0 && preview.fileLines.length > 0 && (
                <OlexManualMoves
                  lines={preview.movableLines}
                  fileLines={preview.fileLines}
                  onMove={(lineId, fileLine) =>
                    changeChoices({ manualMoves: withManualMove(choices.manualMoves, lineId, fileLine) })
                  }
                  onHighlight={setHighlight}
                  t={t}
                />
              )}
              {preview.names.length > 0 && (
                <OlexNameChoice
                  names={preview.names}
                  useOlexNames={choices.useOlexNames}
                  onUseOlexNamesChange={useOlexNames => changeChoices({ useOlexNames })}
                  t={t}
                />
              )}
            </>
          )}
        </AsideScroll>
        <AsideActions>
          {ready && (
            <Typography fontSize={12} color="text.secondary">
              {t('revision-olex-adds-total', { added: addedCount, removed, renamed })}
            </Typography>
          )}
          <ActionRow>
            <CancelButton onClick={onClose} sx={{ flex: 'none' }} />
            <MainButton sx={{ flex: 1 }} loading={isApplying} disabled={!ready || isPreviewing} onClick={handleApply}>
              {t('revision-olex-apply')}
            </MainButton>
          </ActionRow>
        </AsideActions>
      </ModeAside>
    </>
  )
}

export default OlexUploadMode
